// src/components/agencies/AgencyDetails.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchAgency,
  deleteAgency,
  toggleAgencyStatus,
  fetchAgencyServices,
  fetchOpeningHours,
  fetchAgencyDocuments,
  uploadAgencyDocument,
  deleteAgencyDocument,
  clearAgencyError,
} from '../../store/actions/agencyActions';
import { fetchServices } from '../../store/actions/serviceActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Tabs from '../common/Tabs';
import Pagination from '../common/Pagination';
import ServiceManager from './ServiceManager';
import OpeningHoursManager from './OpeningHoursManager';

const AgencyDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { 
    selectedAgency, 
    loading, 
    error, 
    agencyServices, 
    openingHours,
    documents,
    documentsMeta,
    uploadProgress 
  } = useSelector((state) => state.agencies);
  const { services } = useSelector((state) => state.services);

  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showServiceManager, setShowServiceManager] = useState(false);
  const [showHoursManager, setShowHoursManager] = useState(false);
  const [documentsPage, setDocumentsPage] = useState(1);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentDeleteConfirm, setShowDocumentDeleteConfirm] = useState(false);

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
    },
    actionButton: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    editButton: {
      backgroundColor: '#4f46e5',
      color: '#ffffff',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    servicesButton: {
      backgroundColor: '#0891b2',
      color: '#ffffff',
    },
    servicesButtonHover: {
      backgroundColor: '#0e7490',
    },
    hoursButton: {
      backgroundColor: '#b45309',
      color: '#ffffff',
    },
    hoursButtonHover: {
      backgroundColor: '#92400e',
    },
    toggleButton: {
      backgroundColor: '#059669',
      color: '#ffffff',
    },
    toggleButtonHover: {
      backgroundColor: '#047857',
    },
    deleteButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    content: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    hero: {
      padding: '2rem',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    heroContent: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    agencyAvatar: {
      width: '100px',
      height: '100px',
      borderRadius: '50%',
      backgroundColor: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '2.5rem',
      fontWeight: 500,
      color: '#4b5563',
    },
    agencyInfo: {
      flex: 1,
    },
    agencyName: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.5rem',
    },
    entityName: {
      fontSize: '1rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.75rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    openBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    closedBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1.5rem',
      marginTop: '1.5rem',
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '1rem',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#111827',
    },
    rating: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    stars: {
      color: '#fbbf24',
      fontSize: '1.25rem',
    },
    tabContent: {
      padding: '2rem',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '2rem',
    },
    infoSection: {
      marginBottom: '2rem',
    },
    infoTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1rem',
    },
    infoList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    infoItem: {
      display: 'flex',
      padding: '0.75rem 0',
      borderBottom: '1px solid #f3f4f6',
    },
    infoLabel: {
      width: '120px',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
    },
    infoValue: {
      flex: 1,
      fontSize: '0.875rem',
      color: '#111827',
    },
    servicesList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    serviceTag: {
      padding: '0.5rem 1rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '9999px',
      fontSize: '0.875rem',
    },
    hoursGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
    },
    hoursCard: {
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    hoursDay: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '0.5rem',
    },
    hoursTime: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    documentUpload: {
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '2px dashed #d1d5db',
      textAlign: 'center',
    },
    uploadButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    uploadProgress: {
      marginTop: '1rem',
      height: '0.5rem',
      backgroundColor: '#e5e7eb',
      borderRadius: '9999px',
      overflow: 'hidden',
    },
    progressBar: {
      height: '100%',
      backgroundColor: '#2563eb',
      transition: 'width 0.3s ease',
    },
    documentsTable: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    documentsHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    documentsHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
    },
    documentsRow: {
      borderBottom: '1px solid #e5e7eb',
    },
    documentsCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    documentLink: {
      color: '#2563eb',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    documentIcon: {
      width: '1.5rem',
      height: '1.5rem',
      color: '#6b7280',
    },
    documentDeleteButton: {
      padding: '0.25rem',
      backgroundColor: 'transparent',
      border: 'none',
      color: '#dc2626',
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'all 0.2s',
    },
    documentDeleteButtonHover: {
      backgroundColor: '#fee2e2',
    },
    mapContainer: {
      height: '400px',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#6b7280',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchAgency(id));
      dispatch(fetchAgencyServices(id));
      dispatch(fetchOpeningHours(id));
          dispatch(fetchServices({ limit: 100, isActive: true })); // 
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (activeTab === 'documents' && id) {
      dispatch(fetchAgencyDocuments(id, { page: documentsPage, limit: 10 }));
    }
  }, [dispatch, id, activeTab, documentsPage]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteAgency(id));
      navigate('/admin/agencies');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, navigate]);

  const handleToggleStatus = useCallback(async () => {
    try {
      await dispatch(toggleAgencyStatus(id));
    } catch (error) {
      console.error('Toggle status failed:', error);
    }
  }, [dispatch, id]);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      await dispatch(uploadAgencyDocument(id, file));
      // Refresh documents list
      dispatch(fetchAgencyDocuments(id, { page: 1, limit: 10 }));
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploadingFile(false);
    }
  }, [dispatch, id]);

  const handleDocumentDelete = useCallback(async () => {
    if (!selectedDocument) return;

    try {
      await dispatch(deleteAgencyDocument(id, selectedDocument.id));
      setShowDocumentDeleteConfirm(false);
      setSelectedDocument(null);
      // Refresh documents list
      dispatch(fetchAgencyDocuments(id, { page: documentsPage, limit: 10 }));
    } catch (error) {
      console.error('Document delete failed:', error);
    }
  }, [dispatch, id, selectedDocument, documentsPage]);

  const handleServiceUpdate = useCallback(() => {
    setShowServiceManager(false);
    // Refresh services
    dispatch(fetchAgencyServices(id));
  }, [dispatch, id]);

  const handleHoursUpdate = useCallback(() => {
    setShowHoursManager(false);
    // Refresh opening hours
    dispatch(fetchOpeningHours(id));
  }, [dispatch, id]);

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) {
      return '🖼️';
    } else if (fileType?.startsWith('application/pdf')) {
      return '📄';
    } else if (fileType?.startsWith('application/msword') || fileType?.includes('document')) {
      return '📝';
    } else if (fileType?.startsWith('application/vnd.ms-excel') || fileType?.includes('spreadsheet')) {
      return '📊';
    } else {
      return '📎';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading && !selectedAgency) {
    return <LoadingSpinner fullScreen />;
  }

  if (!selectedAgency && !loading) {
    return (
      <div style={styles.container}>
        <Alert
          type="error"
          message="Agence non trouvée"
        />
        <Link to="/admin/agencies" style={styles.backButton}>
          Retour à la liste
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Aperçu' },
    { id: 'services', label: `Services (${agencyServices?.length || 0})` },
    { id: 'hours', label: 'Horaires' },
    { id: 'documents', label: `Documents (${selectedAgency?._count?.documents || 0})` },
    { id: 'location', label: 'Localisation' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/agencies')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Détails de l'agence</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/agencies/${id}/edit`}
            style={{
              ...styles.actionButton,
              ...styles.editButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier
          </Link>
          <button
            onClick={() => setShowServiceManager(true)}
            style={{
              ...styles.actionButton,
              ...styles.servicesButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.servicesButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.servicesButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Services
          </button>
          <button
            onClick={() => setShowHoursManager(true)}
            style={{
              ...styles.actionButton,
              ...styles.hoursButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.hoursButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.hoursButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Horaires
          </button>
          <button
            onClick={handleToggleStatus}
            style={{
              ...styles.actionButton,
              ...styles.toggleButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.toggleButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.toggleButton.backgroundColor;
            }}
          >
            {selectedAgency?.isOpen ? (
              <>
                <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Fermer
              </>
            ) : (
              <>
                <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                Ouvrir
              </>
            )}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              ...styles.actionButton,
              ...styles.deleteButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearAgencyError())}
        />
      )}

      <div style={styles.content}>
        {/* Hero Section */}
        <div style={styles.hero}>
          <div style={styles.heroContent}>
            <div style={styles.agencyAvatar}>
              {selectedAgency.name?.charAt(0).toUpperCase()}
            </div>
            <div style={styles.agencyInfo}>
              <h2 style={styles.agencyName}>{selectedAgency.name}</h2>
              {selectedAgency.entity && (
                <div style={styles.entityName}>
                  Entité: {selectedAgency.entity.name}
                </div>
              )}
              <div>
                <span
                  style={{
                    ...styles.badge,
                    ...(selectedAgency.isOpen ? styles.openBadge : styles.closedBadge),
                  }}
                >
                  {selectedAgency.isOpen ? 'Ouvert' : 'Fermé'}
                </span>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Code interne</div>
              <div style={styles.statValue}>{selectedAgency.codeinte || '-'}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Type</div>
              <div style={styles.statValue}>{selectedAgency.codetypein || '-'}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Services</div>
              <div style={styles.statValue}>{agencyServices?.length || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Documents</div>
              <div style={styles.statValue}>{selectedAgency._count?.documents || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Note</div>
              <div style={styles.rating}>
                <span style={styles.stars}>
                  {'★'.repeat(Math.floor(selectedAgency.rating || 0))}
                  {'☆'.repeat(5 - Math.floor(selectedAgency.rating || 0))}
                </span>
                <span style={styles.statValue}>
                  {selectedAgency.rating?.toFixed(1) || 'N/A'}
                </span>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  ({selectedAgency.reviewCount || 0} avis)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === 'overview' && (
            <div style={styles.infoGrid}>
              {/* Contact Information */}
              <div style={styles.infoSection}>
                <h3 style={styles.infoTitle}>Contact</h3>
                <ul style={styles.infoList}>
                  {selectedAgency.phone && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Téléphone</span>
                      <span style={styles.infoValue}>
                        <a href={`tel:${selectedAgency.phone}`}>{selectedAgency.phone}</a>
                      </span>
                    </li>
                  )}
                  {selectedAgency.email && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Email</span>
                      <span style={styles.infoValue}>
                        <a href={`mailto:${selectedAgency.email}`}>{selectedAgency.email}</a>
                      </span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Address Information */}
              <div style={styles.infoSection}>
                <h3 style={styles.infoTitle}>Adresse</h3>
                <ul style={styles.infoList}>
                  {selectedAgency.address && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Adresse</span>
                      <span style={styles.infoValue}>{selectedAgency.address}</span>
                    </li>
                  )}
                  {(selectedAgency.city || selectedAgency.state || selectedAgency.zipCode) && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Ville</span>
                      <span style={styles.infoValue}>
                        {[selectedAgency.city, selectedAgency.state, selectedAgency.zipCode].filter(Boolean).join(', ')}
                      </span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Location Coordinates */}
              <div style={styles.infoSection}>
                <h3 style={styles.infoTitle}>Coordonnées GPS</h3>
                <ul style={styles.infoList}>
                  {selectedAgency.lat && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Latitude</span>
                      <span style={styles.infoValue}>{selectedAgency.lat}</span>
                    </li>
                  )}
                  {selectedAgency.long && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Longitude</span>
                      <span style={styles.infoValue}>{selectedAgency.long}</span>
                    </li>
                  )}
                  {selectedAgency.distance > 0 && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Distance</span>
                      <span style={styles.infoValue}>{selectedAgency.distance} km</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Additional Information */}
              <div style={styles.infoSection}>
                <h3 style={styles.infoTitle}>Informations supplémentaires</h3>
                <ul style={styles.infoList}>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Créé le</span>
                    <span style={styles.infoValue}>
                      {new Date(selectedAgency.createdAt).toLocaleDateString()}
                    </span>
                  </li>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Modifié le</span>
                    <span style={styles.infoValue}>
                      {new Date(selectedAgency.updatedAt).toLocaleDateString()}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              {agencyServices && agencyServices.length > 0 ? (
                <div style={styles.servicesList}>
                  {agencyServices.map((service) => (
                      <span key={service.id} style={styles.serviceTag}>
                        {service.name}
                      </span>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucun service configuré pour cette agence
                </div>
              )}
            </div>
          )}

          {activeTab === 'hours' && (
            <div>
              {openingHours ? (
                <div style={styles.hoursGrid}>
                  <div style={styles.hoursCard}>
                    <div style={styles.hoursDay}>Lundi</div>
                    <div style={styles.hoursTime}>{openingHours.monday || 'Fermé'}</div>
                  </div>
                  <div style={styles.hoursCard}>
                    <div style={styles.hoursDay}>Mardi</div>
                    <div style={styles.hoursTime}>{openingHours.tuesday || 'Fermé'}</div>
                  </div>
                  <div style={styles.hoursCard}>
                    <div style={styles.hoursDay}>Mercredi</div>
                    <div style={styles.hoursTime}>{openingHours.wednesday || 'Fermé'}</div>
                  </div>
                  <div style={styles.hoursCard}>
                    <div style={styles.hoursDay}>Jeudi</div>
                    <div style={styles.hoursTime}>{openingHours.thursday || 'Fermé'}</div>
                  </div>
                  <div style={styles.hoursCard}>
                    <div style={styles.hoursDay}>Vendredi</div>
                    <div style={styles.hoursTime}>{openingHours.friday || 'Fermé'}</div>
                  </div>
                  <div style={styles.hoursCard}>
                    <div style={styles.hoursDay}>Samedi</div>
                    <div style={styles.hoursTime}>{openingHours.saturday || 'Fermé'}</div>
                  </div>
                  <div style={styles.hoursCard}>
                    <div style={styles.hoursDay}>Dimanche</div>
                    <div style={styles.hoursTime}>{openingHours.sunday || 'Fermé'}</div>
                  </div>
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucun horaire configuré pour cette agence
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div>
              {/* Upload Section */}
              <div style={styles.documentUpload}>
                <input
                  type="file"
                  id="documentUpload"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  disabled={uploadingFile}
                />
                <button
                  onClick={() => document.getElementById('documentUpload').click()}
                  disabled={uploadingFile}
                  style={styles.uploadButton}
                >
                  <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {uploadingFile ? 'Téléchargement...' : 'Télécharger un document'}
                </button>
                {uploadingFile && uploadProgress > 0 && (
                  <div style={styles.uploadProgress}>
                    <div style={{ ...styles.progressBar, width: `${uploadProgress}%` }} />
                  </div>
                )}
              </div>

              {/* Documents List */}
              {documents && documents.length > 0 ? (
                <>
                  <table style={styles.documentsTable}>
                    <thead style={styles.documentsHeader}>
                      <tr>
                        <th style={styles.documentsHeaderCell}>Nom</th>
                        <th style={styles.documentsHeaderCell}>Type</th>
                        <th style={styles.documentsHeaderCell}>Taille</th>
                        <th style={styles.documentsHeaderCell}>Date d'upload</th>
                        <th style={styles.documentsHeaderCell}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc) => (
                        <tr key={doc.id} style={styles.documentsRow}>
                          <td style={styles.documentsCell}>
                            <a
                              href={doc.storagePath}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.documentLink}
                            >
                              <span style={styles.documentIcon}>{getFileIcon(doc.fileType)}</span>
                              {doc.originalName || doc.name}
                            </a>
                          </td>
                          <td style={styles.documentsCell}>{doc.fileType}</td>
                          <td style={styles.documentsCell}>{formatFileSize(doc.fileSize)}</td>
                          <td style={styles.documentsCell}>
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </td>
                          <td style={styles.documentsCell}>
                            <button
                              onClick={() => {
                                setSelectedDocument(doc);
                                setShowDocumentDeleteConfirm(true);
                              }}
                              style={styles.documentDeleteButton}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = styles.documentDeleteButtonHover.backgroundColor;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    currentPage={documentsMeta.page}
                    totalPages={documentsMeta.totalPages}
                    onPageChange={setDocumentsPage}
                  />
                </>
              ) : (
                <div style={styles.emptyState}>
                  Aucun document téléchargé pour cette agence
                </div>
              )}
            </div>
          )}

          {activeTab === 'location' && (
            <div>
              {selectedAgency.lat && selectedAgency.long ? (
                <div style={styles.mapContainer}>
                  {/* Integrate with your preferred map library here */}
                  <iframe
                    title="Agency Location"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0, borderRadius: '0.5rem' }}
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${selectedAgency.long - 0.01}%2C${selectedAgency.lat - 0.01}%2C${selectedAgency.long + 0.01}%2C${selectedAgency.lat + 0.01}&layer=mapnik&marker=${selectedAgency.lat}%2C${selectedAgency.long}`}
                    allowFullScreen
                  />
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucune coordonnée GPS disponible pour cette agence
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Service Manager Modal */}
      {showServiceManager && (
        <ServiceManager
          agencyId={id}
          currentServices={agencyServices}
          allServices={services}
          onClose={() => setShowServiceManager(false)}
          onUpdate={handleServiceUpdate}
        />
      )}

      {/* Opening Hours Manager Modal */}
      {showHoursManager && (
        <OpeningHoursManager
          agencyId={id}
          currentHours={openingHours}
          onClose={() => setShowHoursManager(false)}
          onUpdate={handleHoursUpdate}
        />
      )}

      {/* Delete Agency Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer l'agence"
        message={`Êtes-vous sûr de vouloir supprimer l'agence "${selectedAgency?.name}" ? Cette action supprimera également tous les documents associés.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {/* Delete Document Confirmation */}
      <ConfirmDialog
        isOpen={showDocumentDeleteConfirm}
        onClose={() => {
          setShowDocumentDeleteConfirm(false);
          setSelectedDocument(null);
        }}
        onConfirm={handleDocumentDelete}
        title="Supprimer le document"
        message={`Êtes-vous sûr de vouloir supprimer le document "${selectedDocument?.originalName || selectedDocument?.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default AgencyDetails;